import Coupon from '@alltrails/core/types/Coupon';
import type PurchaseTier from '@alltrails/core/types/PurchaseTier';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';

const promoCampaignValues = ['amex', 'beauvoyage2026', 'expediaexplore2026'] as const;

export type PromoCampaignQueryValue = (typeof promoCampaignValues)[number];

const isPromoCampaignValue = (v: string): v is PromoCampaignQueryValue => promoCampaignValues.includes(v as PromoCampaignQueryValue);

export function resolvePromoCampaignPlansQuery(campaign: string | string[] | undefined): PromoCampaignQueryValue | undefined {
  if (!campaign) {
    return undefined;
  }

  if (Array.isArray(campaign)) {
    return campaign.find(isPromoCampaignValue);
  }

  if (isPromoCampaignValue(campaign)) {
    return campaign;
  }

  return undefined;
}

export function getPlanCheckoutUrl({
  tierSelected,
  isAuthenticated,
  languageRegionCode,
  searchParams,
  couponTier,
  couponCode,
  promoCheckoutCampaign
}: {
  tierSelected: PurchaseTier;
  languageRegionCode: LanguageRegionCode;
  isAuthenticated: boolean;
  searchParams?: URLSearchParams | null;
  couponTier?: Coupon['subscriptionTier'];
  couponCode?: string;
  promoCheckoutCampaign?: PromoCampaignQueryValue;
}) {
  const params = new URLSearchParams(searchParams?.toString());

  // add coupon code to URL if it applies to the selected tier, and there is no coupon code already in the URL
  if (couponCode && !params.get('k') && (couponTier === 'any' || couponTier === tierSelected)) {
    params.set('k', couponCode);
  }

  // Remove coupon code if it doesn't apply to the selected tier
  if (params.get('k') && couponTier !== 'any' && tierSelected !== couponTier) {
    params.delete('k');
  }

  if (tierSelected === 'peak') {
    params.set('tier', 'peak');
  }

  if (tierSelected === 'plus' && params.get('tier')) {
    params.delete('tier');
  }

  // showBothPlans is only relevant on the plans page, not checkout
  params.delete('showBothPlans');

  if (promoCheckoutCampaign === 'amex') {
    params.set('campaign', 'amex');
  }

  const search = params.toString();
  const checkoutPath = `/plans/checkout${search ? `?${search}` : ''}`;

  const signupParams = new URLSearchParams({ returnTo: wrapUrlSafe(checkoutPath, languageRegionCode) });

  if (promoCheckoutCampaign) {
    signupParams.set('hideAppPromotion', 'true');
  }

  return isAuthenticated ? checkoutPath : `/signup?${signupParams}`;
}
